import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Button, Typography, Card, CardContent, CardActions, 
  Container, Grid, IconButton, Dialog, Chip, TextField, 
  FormControl, InputLabel, Select, MenuItem, CircularProgress, 
  Snackbar, Alert, ButtonGroup, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { coursesAPI } from '../services/api';

// Import the new component
import CourseGenerationDialog from '../components/courses/dialogs/CourseGenerationDialog';

// Form validation schema
const CourseSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  description: Yup.string(),
  prerequisite: Yup.string(),
  slug: Yup.string(),
  status: Yup.string().required('Status is required')
});

export default function CourseManagement() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [generationDialogOpen, setGenerationDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await coursesAPI.getAllCourses();
      
      // Fix: Handle the correct response structure with the "courses" property
      if (response.data && Array.isArray(response.data.courses)) {
        setCourses(response.data.courses);
      } else if (Array.isArray(response.data)) {
        setCourses(response.data);
      } else if (response.data && Array.isArray(response.data.data)) {
        setCourses(response.data.data);
      } else {
        // Fallback to empty array if we can't find the courses array
        console.error('Unexpected API response format:', response.data);
        setCourses([]);
        setSnackbar({ 
          open: true, 
          message: 'Unexpected data format received from server', 
          severity: 'error' 
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]); // Ensure courses is always an array
      setSnackbar({ 
        open: true, 
        message: 'Failed to load courses', 
        severity: 'error' 
      });
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingCourse(null);
  };

  const handleOpen = (course = null) => {
    if (course) {
      // Navigate to course detail with state to open edit dialog
      navigate(`/courses/${course.id}`, { state: { openEditDialog: true } });
    } else {
      // For new courses, open the dialog as before
      setEditingCourse(null);
      setOpen(true);
    }
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      if (editingCourse) {
        await coursesAPI.updateCourse(editingCourse.id, values);
        setSnackbar({
          open: true,
          message: 'Course updated successfully',
          severity: 'success'
        });
      } else {
        await coursesAPI.createCourse(values);
        setSnackbar({
          open: true,
          message: 'Course created successfully',
          severity: 'success'
        });
      }
      handleClose();
      resetForm();
      fetchCourses();
    } catch (error) {
      console.error('Error saving course:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${editingCourse ? 'update' : 'create'} course`,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCourse = async (course) => {
    if (window.confirm(`Are you sure you want to delete the course "${course.title}"?`)) {
      try {
        await coursesAPI.deleteCourse(course.id);
        setSnackbar({
          open: true,
          message: 'Course deleted successfully',
          severity: 'success'
        });
        fetchCourses();
      } catch (error) {
        console.error('Error deleting course:', error);
        setSnackbar({
          open: true,
          message: 'Failed to delete course',
          severity: 'error'
        });
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCourseClick = (course) => {
    navigate(`/courses/${course.id}`);
  };

  const handleTogglePublishStatus = async (course) => {
    const newStatus = course.status === 'published' ? 'draft' : 'published';
    try {
      await coursesAPI.updateCourse(course.id, {
        ...course,
        status: newStatus
      });
      setSnackbar({
        open: true,
        message: `Course ${newStatus === 'published' ? 'published' : 'unpublished'} successfully`,
        severity: 'success'
      });
      fetchCourses();
    } catch (error) {
      console.error('Error updating course status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update course status',
        severity: 'error'
      });
    }
  };

  // Add the AI generation handlers
  const handleOpenGenerationDialog = () => {
    setGenerationDialogOpen(true);
  };

  const handleCloseGenerationDialog = () => {
    setGenerationDialogOpen(false);
  };

  const handleGenerateCourse = async (values, { setSubmitting }) => {
    setIsGenerating(true);
    try {
      const response = await coursesAPI.generateCourse({
        name: values.name,
        description: values.description,
        model: values.model
      });
      
      setSnackbar({
        open: true,
        message: 'Course generation job started successfully',
        severity: 'success'
      });
      
      // Don't close dialog yet, let user see job status
      // Don't navigate away, the course isn't ready yet
      
      // Return job information so the dialog can show it
      if (response.data && response.data.jobId) {
        return { jobId: response.data.jobId };
      }
    } catch (error) {
      console.error('Error generating course:', error);
      setSnackbar({
        open: true,
        message: 'Failed to start course generation job',
        severity: 'error'
      });
      handleCloseGenerationDialog();
    } finally {
      setIsGenerating(false);
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">Course Management</Typography>
        <Box>
          <ButtonGroup>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
            >
              New Course
            </Button>
            <Tooltip title="Generate a complete course using AI">
              <Button 
                variant="contained" 
                color="secondary"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleOpenGenerationDialog}
              >
                AI Generate
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Box>
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {Array.isArray(courses) && courses.length > 0 ? (
            courses.map(course => (
              <Grid item xs={12} sm={6} md={4} key={course.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    cursor: 'pointer', // Add pointer cursor to indicate clickability
                    transition: 'all 0.2s ease-in-out', // Add smooth transition for hover effect
                    '&:hover': {
                      transform: 'translateY(-4px)', // Slight lift effect on hover
                      boxShadow: 3 // Enhanced shadow on hover
                    }
                  }}
                  onClick={() => handleCourseClick(course)} // Make entire card clickable
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div" gutterBottom>
                      {course.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {course.description || 'No description provided'}
                    </Typography>
                    <Box mt={1}>
                      <Chip 
                        label={course.status} 
                        color={course.status === 'published' ? 'success' : 'default'} 
                        size="small"
                      />
                    </Box>
                  </CardContent>
                  <CardActions>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click event
                        handleCourseClick(course);
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click event
                        handleOpen(course);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click event
                        handleTogglePublishStatus(course);
                      }}
                    >
                      {course.status === 'published' ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click event
                        handleDeleteCourse(course);
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  No courses found. Create your first course by clicking the "New Course" button.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
      
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <Formik
          initialValues={{
            title: editingCourse?.title || '',
            description: editingCourse?.description || '',
            prerequisite: editingCourse?.prerequisite || '',
            slug: editingCourse?.slug || '',
            status: editingCourse?.status || 'draft'
          }}
          validationSchema={CourseSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              <Box p={3}>
                <Typography variant="h6" gutterBottom>
                  {editingCourse ? 'Edit Course' : 'Create New Course'}
                </Typography>
                
                <Field name="title">
                  {({ field }) => (
                    <TextField
                      {...field}
                      margin="normal"
                      label="Title"
                      fullWidth
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                    />
                  )}
                </Field>
                
                <Field name="description">
                  {({ field }) => (
                    <TextField
                      {...field}
                      margin="normal"
                      label="Description"
                      fullWidth
                      multiline
                      rows={3}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                    />
                  )}
                </Field>
                
                <Field name="prerequisite">
                  {({ field }) => (
                    <TextField
                      {...field}
                      margin="normal"
                      label="Prerequisites"
                      fullWidth
                      error={touched.prerequisite && Boolean(errors.prerequisite)}
                      helperText={touched.prerequisite && errors.prerequisite}
                    />
                  )}
                </Field>
                
                <Field name="slug">
                  {({ field }) => (
                    <TextField
                      {...field}
                      margin="normal"
                      label="Slug"
                      fullWidth
                      error={touched.slug && Boolean(errors.slug)}
                      helperText={touched.slug && errors.slug}
                    />
                  )}
                </Field>
                
                <Field name="status">
                  {({ field }) => (
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Status</InputLabel>
                      <Select
                        {...field}
                        error={touched.status && Boolean(errors.status)}
                      >
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="published">Published</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Field>
                
                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                  <Button onClick={handleClose}>Cancel</Button>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : editingCourse ? 'Update' : 'Create'}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Dialog>
      
      {/* Add the new AI generation dialog */}
      <CourseGenerationDialog
        open={generationDialogOpen}
        onClose={handleCloseGenerationDialog}
        onSubmit={handleGenerateCourse}
        isGenerating={isGenerating}
      />
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
